import { Avatar, Box, Typography } from '@mui/material';
import { IUserMin, IUserSummary } from 'features/settings/users/types';
import React from 'react';

interface OperatorCellProps {
  operator: IUserMin | IUserSummary;
  profileBadgeIndicatorCircle?: boolean;
  showRole?: boolean;
}

/**
 * Pure reference component for the logbook operator profile layout.
 * Extracting this JSX out of the raw configurations array isolates the memory
 * footprints, completely protecting EnhancedTable from infinite loop re-triggers.
 */
export const OperatorCell = React.memo(
  ({ operator, profileBadgeIndicatorCircle = false, showRole = false }: OperatorCellProps) => {
    const nameDisplay = operator.full_name || '—';
    const role = operator.roles || '';
    const initials =
      nameDisplay && nameDisplay !== '—'
        ? nameDisplay
            .split(' ')
            .filter(Boolean)
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()
        : '';

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
        {profileBadgeIndicatorCircle && (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: '#e6f7ff',
              color: '#1890ff',
              border: '1px solid #bae7ff'
            }}>
            {initials}
          </Avatar>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#262626',
              fontSize: '#0.85rem',
              lineHeight: 1.2,
              textTransform: 'capitalize'
            }}>
            {nameDisplay !== '—' ? nameDisplay.toLowerCase() : nameDisplay}
          </Typography>

          {showRole && (
            <Typography variant="caption" sx={{ color: '#8c8c8c', fontSize: '0.75rem' }}>
              {role}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }
);

OperatorCell.displayName = 'OperatorCell';
